import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { numberToWords } from '../utils/formatCurrency';
import { settingsService, salesQuotationService, productService } from '../services/api';
import QuotationPreview from '../components/QuotationPreview';
import Footer from '../components/Footer';
import Select from '../components/Select';
import SearchInput from '../components/SearchInput';

const formatINR = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const QTN_STATUS_META = {
  due:            { label: 'DUE',            bg: '#FEF3C7', color: '#92400E' },
  partially_paid: { label: 'PARTIALLY PAID', bg: '#DBEAFE', color: '#1E40AF' },
  paid:           { label: 'PAID',           bg: '#D1FAE5', color: '#065F46' },
  cancelled:      { label: 'CANCELLED',      bg: '#FEE2E2', color: '#991B1B' },
  waived:         { label: 'WAIVED',         bg: '#F3F4F6', color: '#4B5563' },
};

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const QtnStatusBadge = ({ quotationId, status, schedule, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [dropStyle, setDropStyle] = useState({});
  const ref = React.useRef(null);
  const btnRef = React.useRef(null);
  const meta = QTN_STATUS_META[status] || QTN_STATUS_META.due;

  const hasSchedule = schedule && schedule.length > 0;
  const paidCount = hasSchedule ? schedule.filter(s => s.paid).length : 0;
  const waivedCount = hasSchedule ? schedule.filter(s => s.waived).length : 0;
  const hasWaivable = hasSchedule && schedule.some(s => !s.paid && !s.waived);

  const badgeLabel = (() => {
    if (status === 'cancelled') return 'CANCELLED';
    if (status === 'paid') return 'PAID';
    if (hasSchedule && paidCount > 0) {
      return waivedCount > 0 ? `${paidCount}/${schedule.length} PAID · ${waivedCount} WAIVED` : `${paidCount}/${schedule.length} PAID`;
    }
    return meta.label;
  })();

  const options = hasSchedule
    ? [
        { key: 'due', label: 'Due', meta: QTN_STATUS_META.due },
        ...schedule.map((_, i) => ({
          key: `inst_${i}`,
          label: `${ordinal(i + 1)} installment paid`,
          meta: QTN_STATUS_META.partially_paid,
          installmentIndex: i,
        })),
        ...(hasWaivable ? [{ key: 'waive_remaining', label: 'Waive remaining installment(s)', meta: QTN_STATUS_META.waived }] : []),
        ...(waivedCount > 0 ? [{ key: 'unwaive', label: 'Un-waive installment(s)', meta: QTN_STATUS_META.due }] : []),
        { key: 'cancelled', label: 'Cancelled', meta: QTN_STATUS_META.cancelled },
      ]
    : Object.entries(QTN_STATUS_META).filter(([key]) => !['partially_paid', 'waived'].includes(key)).map(([key, m]) => ({ key, label: m.label, meta: m }));

  const isActive = (opt) => {
    if (hasSchedule && opt.installmentIndex !== undefined) return paidCount === opt.installmentIndex + 1 && status === 'partially_paid';
    return opt.key === status;
  };

  React.useEffect(() => {
    if (!open) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropStyle({
        position: 'fixed',
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
        minWidth: '140px',
        zIndex: 9999,
        ...(spaceBelow < 160
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const handleSelect = async (opt) => {
    setOpen(false);
    if (opt.key === 'waive_remaining') {
      const newSchedule = schedule.map(inst => (inst.paid || inst.waived) ? inst : { ...inst, waived: true });
      const stillPaidCount = schedule.filter(s => s.paid).length;
      await salesQuotationService.updateStatus(quotationId, stillPaidCount > 0 ? 'partially_paid' : 'cancelled', { paymentSchedule: newSchedule });
    } else if (opt.key === 'unwaive') {
      const newSchedule = schedule.map(inst => inst.waived ? { ...inst, waived: false } : inst);
      const paidNow = schedule.filter(s => s.paid).length;
      await salesQuotationService.updateStatus(quotationId, paidNow > 0 ? 'partially_paid' : 'due', { paymentSchedule: newSchedule });
    } else if (opt.installmentIndex !== undefined) {
      const newSchedule = schedule.map((inst, i) => ({ ...inst, paid: i <= opt.installmentIndex, waived: i <= opt.installmentIndex ? false : inst.waived }));
      const allPaid = opt.installmentIndex === schedule.length - 1;
      await salesQuotationService.updateStatus(quotationId, allPaid ? 'paid' : 'partially_paid', { paymentSchedule: newSchedule });
    } else {
      if (opt.key === status) return;
      if (opt.key === 'due' && hasSchedule) {
        const newSchedule = schedule.map(inst => ({ ...inst, paid: false, waived: false }));
        await salesQuotationService.updateStatus(quotationId, 'due', { paymentSchedule: newSchedule });
      } else {
        await salesQuotationService.updateStatus(quotationId, opt.key);
      }
    }
    onUpdate();
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          padding: '3px 8px', fontSize: '11px', fontWeight: 600,
          background: meta.bg, color: meta.color, border: `1px solid ${meta.color}33`,
          borderRadius: '2px', cursor: 'pointer', textTransform: 'uppercase',
          letterSpacing: '0.04em', lineHeight: 1.4, whiteSpace: 'nowrap',
        }}
      >
        {badgeLabel}
        <svg width="7" height="4" viewBox="0 0 7 4" style={{ opacity: 0.6 }}><path d="M0 0l3.5 4L7 0z" fill="currentColor" /></svg>
      </button>
      {open && (
        <div style={{
          ...dropStyle, background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '4px',
        }}>
          {options.map((opt) => (
            <button
              key={opt.key}
              onClick={() => handleSelect(opt)}
              style={{
                display: 'block', width: '100%', padding: '5px 8px', border: 'none',
                background: isActive(opt) ? opt.meta.bg : 'transparent', color: opt.meta.color,
                fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.04em', cursor: 'pointer', borderRadius: '2px',
                textAlign: 'left', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { if (!isActive(opt)) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { if (!isActive(opt)) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// FY for an arbitrary date (distinct from getFY() below, which is only for
// numbering new quotations off "now")
export const getFYOf = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const startYear = m < 3 ? y - 1 : y;
  return `${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
};

const installmentAmount = (inst, grandTotal) => inst.amountOverride !== undefined && inst.amountOverride !== ''
  ? parseFloat(inst.amountOverride)
  : grandTotal * (parseFloat(inst.percent) || 0) / 100;

const getMatchingInstallments = (qtn, filters) => {
  const schedule = qtn.formData?.paymentSchedule;
  if (!schedule || schedule.length === 0 || !filters?.month) return [];
  return schedule.filter(s => s.dueDate
    && (!filters.year || getFYOf(s.dueDate) === filters.year)
    && s.dueDate.slice(5, 7) === filters.month);
};

// Earliest still-unpaid, non-waived installment due date, or the quotation's
// own validity date as a fallback when there's no schedule
export const getNextDueDate = (qtn) => {
  const schedule = qtn.formData?.paymentSchedule;
  if (schedule && schedule.length > 0) {
    const unpaid = schedule.filter(s => !s.paid && !s.waived).sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
    return unpaid.length > 0 ? unpaid[0].dueDate : null;
  }
  return qtn.validUntil || null;
};

export const isQtnOverdue = (qtn, todayStr) => {
  if (qtn.status === 'cancelled' || qtn.status === 'paid') return false;
  const due = getNextDueDate(qtn);
  return !!due && due < todayStr;
};

// Total still owed on this quotation, restricted to the selected month's
// installments when a month filter is active
export const computeQtnDueAmount = (qtn, filters) => {
  const grandTotal = parseFloat(qtn.grandTotal) || 0;
  if (qtn.status === 'cancelled' || qtn.status === 'paid') return 0;
  const schedule = qtn.formData?.paymentSchedule;
  if (schedule && schedule.length > 0) {
    const matches = getMatchingInstallments(qtn, filters);
    const unpaid = (matches.length > 0 ? matches : schedule).filter(s => !s.paid && !s.waived);
    return unpaid.reduce((sum, s) => sum + installmentAmount(s, grandTotal), 0);
  }
  return grandTotal;
};

const FALLBACK_PRESET_ITEMS = [
  { label: 'A1 German Language Course', rate: '14000', gst: 18 },
];

const getFY = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const startYear = m < 3 ? y - 1 : y;
  return `${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
};

const makeQtnNo = (fy, num) => `QTN/${fy}/${String(num).padStart(6, '0')}`;

const defaultItem = () => ({ description: '', qty: '1', rate: '', gst: '18' });

const defaultForm = () => ({
  quotationNumber: '',
  companyName: 'Neuberlinr Private Limited',
  companyAddress: 'No.47, Bharathi Street,\nTiruchirappalli - 620005,\nTamil Nadu',
  companyCIN: 'CIN: U70200TN2026PTC188018',
  companyGSTIN: 'GSTIN: 33AALCN1785B1ZL',
  companyState: 'Tamil Nadu',
  companyStateCode: '33',
  companyPhone: '+91 73389 90669',
  companyEmail: 'madurai@eurocas.org',
  companyWebsite: 'www.eurocas.org',
  customerTo: '',
  quotationDate: today(),
  validUntil: addDays(today(), 30),
  items: [defaultItem()],
  discount: '',
  paymentSchedule: [], // [{ percent: 50, dueDate: '2026-03-17', label: '' }]
  notes: '',
  accountName: 'NEUBERLINR PRIVATE LIMITED',
  accountNumber: '5020 0118 4521 33',
  bankName: 'HDFC Bank',
  ifsc: 'HDFC0001177',
  branch: 'CHETTIKULAM JUNCTION',
});

// Renders the quotation preview element into a (multi-page) A4 PDF
export const generateQuotationPdf = async (el, filename) => {
  const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: el.scrollWidth, height: el.scrollHeight });
  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfW = 210;
  const pdfH = 297;
  const imgW = pdfW;
  const imgH = (canvas.height * pdfW) / canvas.width;
  if (imgH <= pdfH + 1) {
    pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH);
  } else {
    let yOffset = 0;
    while (yOffset < canvas.height) {
      const pageCanvas = document.createElement('canvas');
      const sliceH = Math.min(canvas.height - yOffset, (canvas.width * pdfH) / pdfW);
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceH;
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, sliceH);
      ctx.drawImage(canvas, 0, yOffset, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
      const pageImg = pageCanvas.toDataURL('image/jpeg', 0.95);
      if (yOffset > 0) pdf.addPage();
      pdf.addImage(pageImg, 'JPEG', 0, 0, imgW, (sliceH * pdfW) / canvas.width);
      yOffset += sliceH;
    }
  }
  pdf.save(filename);
};

// embedded: rendered inside the combined invoices page, which owns the list.
// `action` opens a quotation ({ type: 'view' | 'edit', qtn }) or a new one
// ({ type: 'new' }); `onExit` fires when the user goes back to the list.
const Quotations = ({ embedded = false, action = null, onExit }) => {
  const [form, setForm] = useState(() => {
    if (action?.type === 'edit') return { ...defaultForm(), ...action.qtn.formData, quotationNumber: action.qtn.quotationNumber };
    try {
      const saved = sessionStorage.getItem('quotationForm');
      if (saved) return { ...defaultForm(), ...JSON.parse(saved) };
    } catch {}
    return defaultForm();
  });
  const [editingFrom, setEditingFrom] = useState(false);
  const [editingQtnNo, setEditingQtnNo] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [showPreview, setShowPreview] = useState(() => window.location.hash === '#qtn-preview');
  const [savedQuotations, setSavedQuotations] = useState([]);
  const [activeTab, setActiveTab] = useState(action?.type === 'new' ? 'form' : 'list');
  const [viewingQuotation, setViewingQuotation] = useState(action?.type === 'view' ? action.qtn : null);
  const [editingQuotationId, setEditingQuotationId] = useState(action?.type === 'edit' ? action.qtn.id : null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);
  const dlPreviewRef = useRef(null);
  const [dlQuotation, setDlQuotation] = useState(null);
  const [installmentCount, setInstallmentCount] = useState(2); // min 2
  const [PRESET_ITEMS, setPresetItems] = useState(FALLBACK_PRESET_ITEMS);

  const currentFY = getFY();
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [qtnFilters, setQtnFilters] = useState({ search: '', year: '', month: currentMonth, startDate: '', endDate: '' });

  const qtnHasActiveFilters = qtnFilters.search || qtnFilters.startDate || qtnFilters.endDate
    || qtnFilters.year || qtnFilters.month;
  const clearQtnFilters = () => setQtnFilters({ search: '', year: '', month: '', startDate: '', endDate: '' });

  const qtnYearOptions = useMemo(() => {
    const fys = [...new Set(savedQuotations.map(q => q.quotationDate && getFYOf(q.quotationDate)).filter(Boolean))];
    return fys.sort((a, b) => b.localeCompare(a));
  }, [savedQuotations]);

  const qtnAvailableMonths = useMemo(() => {
    const set = new Set();
    const inYear = (dateStr) => !qtnFilters.year || (dateStr && getFYOf(dateStr) === qtnFilters.year);
    savedQuotations.forEach(q => {
      if (q.quotationDate && inYear(q.quotationDate)) set.add(q.quotationDate.slice(5, 7));
      (q.formData?.paymentSchedule || []).forEach(s => {
        if (s.dueDate && inYear(s.dueDate)) set.add(s.dueDate.slice(5, 7));
      });
    });
    if (!qtnFilters.year || qtnFilters.year === currentFY) set.add(currentMonth);
    return set;
  }, [savedQuotations, qtnFilters.year, currentFY, currentMonth]);

  const filteredQuotations = useMemo(() => {
    let list = [...savedQuotations];
    const { search, year, month, startDate, endDate } = qtnFilters;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(qtn =>
        qtn.quotationNumber?.toLowerCase().includes(q) ||
        qtn.customerName?.toLowerCase().includes(q) ||
        String(qtn.grandTotal || '').includes(q)
      );
    }
    if (year) list = list.filter(qtn => {
      if (qtn.quotationDate && getFYOf(qtn.quotationDate) === year) return true;
      return (qtn.formData?.paymentSchedule || []).some(s => s.dueDate && getFYOf(s.dueDate) === year);
    });
    if (month) list = list.filter(qtn => {
      if (qtn.quotationDate?.slice(5, 7) === month) return true;
      if ((qtn.formData?.paymentSchedule || []).some(s => s.dueDate?.slice(5, 7) === month)) return true;
      if (month === currentMonth && isQtnOverdue(qtn, today())) return true;
      return false;
    });
    if (startDate) list = list.filter(qtn => qtn.quotationDate >= startDate);
    if (endDate) list = list.filter(qtn => qtn.quotationDate <= endDate);
    return list;
  }, [savedQuotations, qtnFilters, currentMonth]);

  const pendingTotal = filteredQuotations.reduce((sum, qtn) => sum + computeQtnDueAmount(qtn, qtnFilters), 0);

  const cancelledTotal = filteredQuotations
    .filter(qtn => qtn.status === 'cancelled')
    .reduce((sum, qtn) => sum + (parseFloat(qtn.grandTotal) || 0), 0);

  const receivedBalance = filteredQuotations.reduce((acc, qtn) => {
    if (qtn.status === 'cancelled') return acc;
    const sub = parseFloat(qtn.subtotal || 0);
    const gst = parseFloat(qtn.totalGst || 0);
    const schedule = qtn.formData?.paymentSchedule || [];
    const { year, month } = qtnFilters;
    const instInFilter = (s) => {
      if (year && (!s.dueDate || getFYOf(s.dueDate) !== year)) return false;
      if (month && s.dueDate?.slice(5, 7) !== month) return false;
      return true;
    };
    if (qtn.status === 'paid') {
      if (schedule.length > 0) {
        const pct = schedule.filter(instInFilter).reduce((s, p) => s + parseFloat(p.percent || 0), 0) / 100;
        acc.sub += sub * pct; acc.gst += gst * pct;
      } else {
        acc.sub += sub; acc.gst += gst;
      }
    } else if (qtn.status === 'partially_paid') {
      const pct = schedule.filter(s => s.paid && instInFilter(s)).reduce((s, p) => s + parseFloat(p.percent || 0), 0) / 100;
      acc.sub += sub * pct; acc.gst += gst * pct;
    }
    return acc;
  }, { sub: 0, gst: 0 });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const setItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, defaultItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const itemSubtotal = (it) => (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0);
  const itemGst = (it) => { const g = parseFloat(it.gst) || 0; return itemSubtotal(it) * g / 100; };
  const itemTotal = (it) => itemSubtotal(it) + itemGst(it);
  const subtotal = form.items.reduce((s, it) => s + itemSubtotal(it), 0);
  const totalGst = form.items.reduce((s, it) => s + itemGst(it), 0);
  const discount = Math.max(0, Math.min(parseFloat(form.discount) || 0, subtotal));
  const grandTotal = subtotal - discount + totalGst;

  const canDownload = form.customerTo.trim() && form.items.some(it => it.description.trim() && (parseFloat(it.rate) || 0) > 0);

  const loadQuotations = useCallback(async () => {
    try {
      const res = await salesQuotationService.list();
      setSavedQuotations(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const loadQtnNo = async () => {
      if (form.quotationNumber) return;
      try {
        const fy = getFY();
        const val = await settingsService.get(`quotation_counter_${fy}`);
        const next = (parseInt(val || '0', 10)) + 1;
        setForm(f => ({ ...f, quotationNumber: makeQtnNo(fy, next) }));
      } catch {
        setForm(f => ({ ...f, quotationNumber: makeQtnNo(getFY(), 1) }));
      }
    };
    loadQtnNo();
    loadQuotations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load products from DB
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await productService.list();
        const products = res.data || [];
        if (products.length > 0) {
          setPresetItems(products.filter(p => p.active !== false).map(p => ({ label: p.name, rate: String(p.rate), gst: parseFloat(p.gst) })));
        }
      } catch {}
    };
    loadProducts();
  }, []);

  useEffect(() => {
    // Embedded under the invoice list, leave the invoice page's hash alone
    if (embedded && !showPreview && window.location.hash !== '#qtn-preview') return;
    window.location.hash = showPreview ? '#qtn-preview' : '';
  }, [showPreview, embedded]);

  useEffect(() => {
    sessionStorage.setItem('quotationForm', JSON.stringify(form));
  }, [form]);

  // Embedded: the parent owns the list, so landing back on it means we're done
  useEffect(() => {
    if (embedded && activeTab === 'list' && !viewingQuotation && !editingQuotationId) onExit?.();
  }, [embedded, activeTab, viewingQuotation, editingQuotationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save quotation (edit mode)
  const saveQuotation = useCallback(async () => {
    if (!canDownload || !editingQuotationId) return;
    setSaving(true);
    try {
      const customerFirstLine = (form.customerTo || '').split('\n')[0].trim();
      await salesQuotationService.update(editingQuotationId, {
        quotationNumber: form.quotationNumber,
        customerName: customerFirstLine,
        quotationDate: form.quotationDate,
        validUntil: form.validUntil,
        subtotal, totalGst, grandTotal,
        items: form.items,
        notes: form.notes,
        formData: form,
      });
      await loadQuotations();
      setEditingQuotationId(null);
      setShowPreview(false);
    } catch (e) {
      console.error('Failed to save quotation:', e);
    } finally {
      setSaving(false);
    }
  }, [canDownload, editingQuotationId, form, subtotal, totalGst, grandTotal, loadQuotations]);

  // Generate & Download PDF
  const generatePdfFromRef = useCallback(generateQuotationPdf, []);

  const downloadPDF = useCallback(async () => {
    if (!canDownload || !previewRef.current) return;
    setGenerating(true);
    try {
      const fy = getFY();
      const parts = form.quotationNumber.split('/');
      const currentNum = parseInt(parts[parts.length - 1], 10) || 1;
      await settingsService.set(`quotation_counter_${fy}`, String(currentNum));
    } catch {}
    try {
      await generatePdfFromRef(previewRef.current, form.quotationNumber.replace(/\//g, '-') + '.pdf');
      // Save record
      try {
        const customerFirstLine = (form.customerTo || '').split('\n')[0].trim();
        const payload = {
          quotationNumber: form.quotationNumber,
          customerName: customerFirstLine,
          quotationDate: form.quotationDate,
          validUntil: form.validUntil,
          subtotal, totalGst, grandTotal,
          items: form.items,
          notes: form.notes,
          formData: form,
        };
        const existing = savedQuotations.find(q => q.quotationNumber === form.quotationNumber);
        if (existing) {
          await salesQuotationService.update(existing.id, payload);
        } else {
          await salesQuotationService.create(payload);
        }
        await loadQuotations();
        if (editingQuotationId) {
          setEditingQuotationId(null);
          setShowPreview(false);
        }
      } catch (e) { console.error('Failed to save quotation record:', e); }
    } catch (e) { console.error('PDF generation failed:', e); }
    finally { setGenerating(false); }
  }, [canDownload, form, subtotal, totalGst, grandTotal, loadQuotations, savedQuotations, editingQuotationId, generatePdfFromRef]);

  const downloadExistingQuotation = useCallback(async (qtn) => {
    setDlQuotation(qtn);
    await new Promise(r => setTimeout(r, 200));
    const el = dlPreviewRef.current;
    if (!el) return;
    try {
      await generatePdfFromRef(el, qtn.quotationNumber.replace(/\//g, '-') + '.pdf');
    } catch (e) { console.error('PDF generation failed:', e); }
    finally { setDlQuotation(null); }
  }, [generatePdfFromRef]);

  const showForm = activeTab === 'form' || (activeTab === 'list' && editingQuotationId);

  return (
    <>
      <div className={embedded ? undefined : 'main-content'}>
        <div className={embedded ? undefined : 'container'}>
          {/* Header */}
          <div className="flex-between mb-lg">
            <div>
              {!embedded && <h1 style={{ marginBottom: '4px' }}>Quotations</h1>}
              {(activeTab === 'form' || editingQuotationId) && form.quotationNumber && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  {editingQtnNo ? (
                    <>
                      <input className="form-input" value={form.quotationNumber} onChange={e => set('quotationNumber', e.target.value)} style={{ width: '180px', fontSize: 'var(--font-size-sm)', padding: '3px 8px' }} />
                      <button onClick={() => setEditingQtnNo(false)} className="btn btn-secondary btn-sm" style={{ padding: '3px 10px', fontSize: '12px' }}>Done</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-weight-medium)' }}>{form.quotationNumber}{editingQuotationId && <span style={{ color: 'var(--color-primary)', marginLeft: '6px', fontSize: '11px' }}>(editing)</span>}</span>
                      {!editingQuotationId && <button onClick={() => setEditingQtnNo(true)} className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }}>Edit</button>}
                    </>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
              {!embedded && <>
              {cancelledTotal > 0 && (
                <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#991B1B', lineHeight: 1.2 }}>{formatINR(cancelledTotal)}</div>
                  <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Cancelled</div>
                </div>
              )}
              {receivedBalance.sub + receivedBalance.gst > 0 && (
                <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#065F46', lineHeight: 1.2 }}>{formatINR(receivedBalance.sub + receivedBalance.gst)}</div>
                  <div style={{ fontSize: '11px', color: '#047857', marginTop: '2px' }}>{formatINR(receivedBalance.sub)} + GST {formatINR(receivedBalance.gst)}</div>
                </div>
              )}
              {pendingTotal > 0 && (
                <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#92400E', lineHeight: 1.2 }}>{formatINR(pendingTotal)}</div>
                  <div style={{ fontSize: '11px', color: '#B45309', marginTop: '2px' }}>Pending Payment</div>
                </div>
              )}
              {(receivedBalance.sub + receivedBalance.gst + pendingTotal) > 0 && (
                <div style={{ background: '#FFEDD5', border: '1px solid #FDBA74', borderRadius: '8px', padding: '8px 16px', textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#9A3412', lineHeight: 1.2 }}>{formatINR(receivedBalance.sub + receivedBalance.gst + pendingTotal)}</div>
                  <div style={{ fontSize: '11px', color: '#C2410C', marginTop: '2px' }}>Total Collectable</div>
                </div>
              )}
              </>}
              {activeTab === 'form' && !editingQuotationId && canDownload && (
                <button onClick={() => setShowPreview(p => !p)} className="btn btn-secondary">
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          {!embedded && <div style={{ display: 'flex', gap: '2px', marginBottom: 'var(--spacing-lg)', borderBottom: '2px solid var(--color-gray-200)' }}>
            <button
              onClick={() => { setActiveTab('list'); setShowPreview(false); setViewingQuotation(null); setEditingQuotationId(null); }}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'list' && !editingQuotationId ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'list' && !editingQuotationId ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'list' && !editingQuotationId ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              Quotations
              {savedQuotations.length > 0 && (
                <span style={{ background: 'var(--color-gray-200)', borderRadius: '2px', padding: '1px 7px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)' }}>
                  {savedQuotations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('form'); setViewingQuotation(null); setEditingQuotationId(null); }}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'form' || editingQuotationId ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'form' || editingQuotationId ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'form' || editingQuotationId ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)' }}
            >
              {editingQuotationId ? 'Edit Quotation' : 'New Quotation'}
            </button>
          </div>}

          {/* Quotations List */}
          {!embedded && activeTab === 'list' && !viewingQuotation && !editingQuotationId && (
            savedQuotations.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-text">No quotations yet</div>
                <button onClick={() => setActiveTab('form')} className="btn btn-primary mt-md">Create First Quotation</button>
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="card mb-lg">
                  <div className="flex gap-md" style={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
                    <div style={{ flex: '1 1 260px', display: 'flex' }}>
                      <SearchInput
                        placeholder="Search quotation no, customer, amount..."
                        value={qtnFilters.search}
                        onChange={v => setQtnFilters(f => ({ ...f, search: v }))}
                        inputStyle={{ height: '100%' }}
                      />
                    </div>
                    <div style={{ flex: '0 1 auto', display: 'flex' }}>
                      <Select
                        value={qtnFilters.year}
                        onChange={v => setQtnFilters(f => ({ ...f, year: v, month: '' }))}
                        options={[{ value: '', label: 'All FYs' }, ...qtnYearOptions.map(y => ({ value: y, label: `FY ${y}` }))]}
                        style={{ minWidth: '110px' }}
                      />
                    </div>
                    <div style={{ flex: '0 1 auto', display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', padding: '0 var(--spacing-xs)' }}>
                      <input type="date" style={{ border: 'none', padding: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}
                        value={qtnFilters.startDate} onChange={e => setQtnFilters(f => ({ ...f, startDate: e.target.value }))} />
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>to</span>
                      <input type="date" style={{ border: 'none', padding: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}
                        value={qtnFilters.endDate} onChange={e => setQtnFilters(f => ({ ...f, endDate: e.target.value }))} />
                    </div>
                    <button onClick={clearQtnFilters} className="btn btn-secondary btn-sm" disabled={!qtnHasActiveFilters} style={{ alignSelf: 'stretch' }}>
                      Clear Filters
                    </button>
                  </div>
                  {qtnAvailableMonths.size > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)', marginTop: 'var(--spacing-md)' }}>
                      <button onClick={() => setQtnFilters(f => ({ ...f, month: '' }))} className={`month-chip${!qtnFilters.month ? ' month-chip-active' : ''}`}>All</button>
                      {MONTH_LABELS.map((label, i) => {
                        const val = String(i + 1).padStart(2, '0');
                        if (!qtnAvailableMonths.has(val)) return null;
                        return (
                          <button key={val} onClick={() => setQtnFilters(f => ({ ...f, month: val }))} className={`month-chip${qtnFilters.month === val ? ' month-chip-active' : ''}`}>{label}</button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex-between mb-md" style={{ alignItems: 'center' }}>
                  <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {filteredQuotations.length} quotation{filteredQuotations.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="card" style={{ overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Quotation No</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Customer</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Date</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Valid Until</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Amount</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Due Date</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Due Amount</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Status</th>
                        <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuotations.map(qtn => {
                        const dueAmount = computeQtnDueAmount(qtn, qtnFilters);
                        const isFullyPaid = qtn.status === 'paid';
                        const isOverdue = !isFullyPaid && qtn.status !== 'cancelled' && isQtnOverdue(qtn, today());
                        const nextDue = getNextDueDate(qtn);
                        const isDueSoon = !isFullyPaid && !isOverdue && qtn.status !== 'cancelled' && nextDue && nextDue <= addDays(today(), 5);
                        const rowBg = qtn.status === 'cancelled' ? '#F3E9DC' : isFullyPaid ? '#ECFDF5' : isOverdue ? '#FEF2F2' : isDueSoon ? '#FEF9C3' : undefined;
                        return (
                          <tr key={qtn.id} style={{ borderBottom: '1px solid var(--color-gray-100)', background: rowBg }}>
                            <td style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>{qtn.quotationNumber}</td>
                            <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>{qtn.customerName}</td>
                            <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>{new Date(qtn.quotationDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                            <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>{qtn.validUntil ? new Date(qtn.validUntil).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                            <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)' }}>{formatINR(qtn.grandTotal)}</td>
                            <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: isOverdue ? '#B91C1C' : 'var(--color-secondary)' }}>{nextDue ? new Date(nextDue).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                            <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', color: dueAmount > 0 ? '#B91C1C' : 'var(--color-secondary)' }}>{formatINR(dueAmount)}</td>
                            <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}>
                              <QtnStatusBadge quotationId={qtn.id} status={qtn.status} schedule={qtn.formData?.paymentSchedule} onUpdate={loadQuotations} />
                            </td>
                            <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                <button onClick={() => setViewingQuotation(qtn)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>View</button>
                                <button onClick={() => downloadExistingQuotation(qtn)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>Download</button>
                                <button onClick={() => {
                                  setForm({ ...defaultForm(), ...qtn.formData, quotationNumber: qtn.quotationNumber });
                                  setViewingQuotation(null);
                                  setEditingQuotationId(qtn.id);
                                  setShowPreview(false);
                                }} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>Edit</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredQuotations.length === 0 && (
                    <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                      No quotations match your filters.
                    </div>
                  )}
                </div>
              </>
            )
          )}

          {/* View quotation */}
          {activeTab === 'list' && viewingQuotation && !editingQuotationId && (
            <>
              <div style={{ marginBottom: 'var(--spacing-md)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button onClick={() => setViewingQuotation(null)} className="btn btn-secondary btn-sm">Back to list</button>
                <button onClick={() => downloadExistingQuotation(viewingQuotation)} className="btn btn-primary btn-sm">Generate &amp; Download</button>
              </div>
              <div className="card mb-md" style={{ padding: 0, overflow: 'hidden' }}>
                <QuotationPreview
                  form={viewingQuotation.formData}
                  subtotal={parseFloat(viewingQuotation.subtotal)}
                  discount={parseFloat(viewingQuotation.formData?.discount) || 0}
                  totalGst={parseFloat(viewingQuotation.totalGst)}
                  grandTotal={parseFloat(viewingQuotation.grandTotal)}
                  itemSubtotal={itemSubtotal}
                  itemGst={itemGst}
                  itemTotal={itemTotal}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                <button onClick={() => setViewingQuotation(null)} className="btn btn-secondary">Back to list</button>
                <button onClick={() => downloadExistingQuotation(viewingQuotation)} className="btn btn-primary">Generate &amp; Download</button>
              </div>
            </>
          )}

          {/* Edit back button */}
          {activeTab === 'list' && editingQuotationId && (
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <button onClick={() => setEditingQuotationId(null)} className="btn btn-secondary btn-sm" style={{ marginBottom: 'var(--spacing-md)' }}>Back to list</button>
            </div>
          )}

          {/* Form / Preview */}
          {showForm && <>

          {showPreview && canDownload ? (
            <>
              <div className="card mb-md" style={{ padding: 0, overflow: 'hidden' }}>
                <QuotationPreview
                  ref={previewRef}
                  form={form}
                  subtotal={subtotal}
                  discount={discount}
                  totalGst={totalGst}
                  grandTotal={grandTotal}
                  itemSubtotal={itemSubtotal}
                  itemGst={itemGst}
                  itemTotal={itemTotal}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                <button onClick={() => setShowPreview(false)} className="btn btn-secondary">Back to Edit</button>
                <button onClick={downloadPDF} disabled={generating} className="btn btn-primary">
                  {generating ? 'Generating...' : 'Generate & Download'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* From + To */}
              <div className="card mb-md">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-lg)' }}>
                  {/* From */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="card-header">From</div>
                      <button onClick={() => setEditingFrom(e => !e)} className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }}>
                        {editingFrom ? 'Done' : 'Edit'}
                      </button>
                    </div>
                    {editingFrom ? (
                      <>
                        <div className="form-group"><label className="form-label">Company Name</label><input className="form-input" value={form.companyName} onChange={e => set('companyName', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Address</label><textarea className="form-input" style={{ minHeight: '60px', resize: 'vertical' }} value={form.companyAddress} onChange={e => set('companyAddress', e.target.value)} /></div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
                          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.companyPhone} onChange={e => set('companyPhone', e.target.value)} /></div>
                          <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.companyEmail} onChange={e => set('companyEmail', e.target.value)} /></div>
                          <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.companyWebsite} onChange={e => set('companyWebsite', e.target.value)} /></div>
                          <div className="form-group"><label className="form-label">CIN</label><input className="form-input" value={form.companyCIN} onChange={e => set('companyCIN', e.target.value)} /></div>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">GSTIN</label><input className="form-input" value={form.companyGSTIN} onChange={e => set('companyGSTIN', e.target.value)} /></div>
                      </>
                    ) : (
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-base)' }}>{form.companyName}</div>
                        {form.companyAddress && <>
                          <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)', marginTop: 'var(--spacing-xs)' }}>Head Office:</div>
                          <div style={{ whiteSpace: 'pre-line' }}>{form.companyAddress}</div>
                        </>}
                        {form.companyPhone && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Ph:</span> {form.companyPhone}</div>}
                        {form.companyEmail && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Email:</span> {form.companyEmail}</div>}
                        {form.companyWebsite && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Website:</span> {form.companyWebsite}</div>}
                        {form.companyCIN && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>CIN:</span> {form.companyCIN.replace(/^CIN:\s*/i, '')}</div>}
                        {form.companyGSTIN && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>GSTIN:</span> {form.companyGSTIN.replace(/^GSTIN:\s*/i, '')}</div>}
                      </div>
                    )}
                  </div>

                  {/* To */}
                  <div>
                    <div className="card-header">To <span style={{ color: '#DC2626', fontSize: 'var(--font-size-xs)' }}>*</span></div>
                    <textarea className="form-input" style={{ minHeight: '180px', resize: 'vertical', borderColor: !form.customerTo.trim() ? '#DC2626' : undefined, boxShadow: !form.customerTo.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} value={form.customerTo} onChange={e => set('customerTo', e.target.value)} placeholder={"Client name\nAddress\nCity, State, PIN"} />
                  </div>
                </div>
              </div>

              {/* Quotation Details */}
              <div className="card mb-md">
                <div className="card-header">Quotation Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Quotation Date</label>
                    <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                      <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('quotationDate', addDays(form.quotationDate, -1))}>-</button>
                      <input type="date" className="form-input" style={{ flex: 1 }} value={form.quotationDate} onChange={e => set('quotationDate', e.target.value)} />
                      <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('quotationDate', addDays(form.quotationDate, 1))}>+</button>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Valid Until</label>
                    <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                      <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('validUntil', addDays(form.validUntil, -1))}>-</button>
                      <input type="date" className="form-input" style={{ flex: 1 }} value={form.validUntil} onChange={e => set('validUntil', e.target.value)} />
                      <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => set('validUntil', addDays(form.validUntil, 1))}>+</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="card mb-md">
                <div className="card-header">Items <span style={{ color: '#DC2626', fontSize: 'var(--font-size-xs)' }}>*</span></div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Description</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '80px' }}>Qty</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '100px' }}>Rate</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '70px' }}>GST %</th>
                        <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '120px' }}>Amount</th>
                        <th style={{ width: '40px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <select
                              className="form-select"
                              value={PRESET_ITEMS.some(p => p.label === item.description) ? item.description : '__custom'}
                              onChange={e => {
                                const val = e.target.value;
                                if (val === '__custom') { setItem(i, 'description', ''); }
                                else {
                                  const preset = PRESET_ITEMS.find(p => p.label === val);
                                  setForm(f => { const items = [...f.items]; items[i] = { ...items[i], description: val, rate: preset ? preset.rate : items[i].rate, gst: preset ? String(preset.gst) : items[i].gst }; return { ...f, items }; });
                                }
                              }}
                              style={{ marginBottom: 'var(--spacing-xs)', borderColor: !item.description.trim() ? '#DC2626' : undefined, boxShadow: !item.description.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }}
                            >
                              {PRESET_ITEMS.map(p => (<option key={p.label} value={p.label}>{p.label}</option>))}
                              <option value="__custom">Custom...</option>
                            </select>
                            {!PRESET_ITEMS.some(p => p.label === item.description) && (
                              <input className="form-input" value={item.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="Item description" style={{ borderColor: !item.description.trim() ? '#DC2626' : undefined, boxShadow: !item.description.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} />
                            )}
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <input className="form-input no-spinner" type="number" min="0" step="0.01" value={item.qty} onChange={e => setItem(i, 'qty', e.target.value)} style={{ textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <input className="form-input no-spinner" type="number" min="0" step="0.01" value={item.rate} onChange={e => setItem(i, 'rate', e.target.value)} style={{ textAlign: 'right', borderColor: !(parseFloat(item.rate) > 0) ? '#DC2626' : undefined, boxShadow: !(parseFloat(item.rate) > 0) ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} />
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                            <input className="form-input no-spinner" type="number" min="0" step="1" value={item.gst} onChange={e => setItem(i, 'gst', e.target.value)} placeholder="0" style={{ textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>
                            {formatINR(itemTotal(item))}
                          </td>
                          <td style={{ padding: 'var(--spacing-xs)', textAlign: 'center' }}>
                            {form.items.length > 1 && (
                              <button onClick={() => removeItem(i)} className="btn btn-secondary btn-sm" style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1 }}>&times;</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 'var(--spacing-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <button onClick={addItem} className="btn btn-secondary btn-sm">+ Add Item</button>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Subtotal: {formatINR(subtotal)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--spacing-xs)', marginTop: '2px' }}>
                      <label htmlFor="quotation-discount" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Discount:</label>
                      <input
                        id="quotation-discount"
                        className="form-input no-spinner"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.discount}
                        onChange={e => set('discount', e.target.value)}
                        placeholder="0"
                        style={{ width: '100px', textAlign: 'right' }}
                      />
                    </div>
                    {discount > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>Subtotal After Discount: {formatINR(subtotal - discount)}</div>}
                    {totalGst > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>GST: {formatINR(totalGst)}</div>}
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', marginTop: '2px' }}>Total: {formatINR(grandTotal)}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginTop: '2px' }}>{numberToWords(grandTotal)}</div>
                  </div>
                </div>
              </div>

              {/* Payment Schedule */}
              {(() => {
                const splitEqually = (n, baseDate) => {
                  const base = parseFloat(n) || 2;
                  const perPct = Math.floor(10000 / base) / 100; // e.g. 3 → 33.33
                  const lastPct = parseFloat((100 - perPct * (base - 1)).toFixed(2));
                  return Array.from({ length: base }, (_, i) => ({
                    label: `Installment ${i + 1}`,
                    percent: String(i === base - 1 ? lastPct : perPct),
                    dueDate: addDays(baseDate, 30 * i),
                  }));
                };
                return (
                  <div className="card mb-md">
                    <div className="card-header" style={{ borderBottom: form.paymentSchedule.length > 0 ? undefined : 'none', marginBottom: form.paymentSchedule.length > 0 ? undefined : 0, paddingBottom: form.paymentSchedule.length > 0 ? undefined : 0 }}>Payment Schedule</div>
                    {form.paymentSchedule.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                          <button type="button" onClick={() => setInstallmentCount(c => Math.max(2, c - 1))} className="btn btn-secondary" style={{ border: 'none', borderRadius: 0, padding: '4px 10px', fontSize: '16px', lineHeight: 1 }}>−</button>
                          <span style={{ padding: '4px 12px', fontWeight: 600, fontSize: 'var(--font-size-sm)', minWidth: '32px', textAlign: 'center' }}>{installmentCount}</span>
                          <button type="button" onClick={() => setInstallmentCount(c => Math.min(24, c + 1))} className="btn btn-secondary" style={{ border: 'none', borderRadius: 0, padding: '4px 10px', fontSize: '16px', lineHeight: 1 }}>+</button>
                        </div>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>installments</span>
                        <button
                          onClick={() => {
                            const s = splitEqually(installmentCount, form.quotationDate);
                            set('paymentSchedule', s);
                          }}
                          className="btn btn-primary btn-sm"
                          style={{ fontSize: '11px', padding: '2px 14px' }}
                        >
                          Split Equally
                        </button>
                      </div>
                    ) : (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 'var(--spacing-sm)' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Installment</th>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '80px' }}>%</th>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '120px' }}>Amount</th>
                              <th style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', width: '160px' }}>Due Date</th>
                              <th style={{ width: '40px' }} />
                            </tr>
                          </thead>
                          <tbody>
                            {form.paymentSchedule.map((ps, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input className="form-input" value={ps.label} onChange={e => {
                                    const s = [...form.paymentSchedule]; s[i] = { ...s[i], label: e.target.value }; set('paymentSchedule', s);
                                  }} placeholder={`Installment ${i + 1}`} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input className="form-input no-spinner" type="number" min="0" max="100" value={ps.percent} onChange={e => {
                                    const s = [...form.paymentSchedule]; s[i] = { ...s[i], percent: e.target.value, amountOverride: undefined }; set('paymentSchedule', s);
                                  }} style={{ textAlign: 'right' }} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input className="form-input no-spinner" type="number" min="0"
                                    value={ps.amountOverride !== undefined ? ps.amountOverride : Math.round(grandTotal * (parseFloat(ps.percent) || 0) / 100 * 100) / 100}
                                    onChange={e => {
                                      const pct = grandTotal > 0 ? Math.round((parseFloat(e.target.value) || 0) / grandTotal * 10000) / 100 : 0;
                                      const s = [...form.paymentSchedule]; s[i] = { ...s[i], percent: pct, amountOverride: e.target.value }; set('paymentSchedule', s);
                                    }}
                                    onBlur={e => {
                                      const s = [...form.paymentSchedule]; s[i] = { ...s[i], amountOverride: undefined }; set('paymentSchedule', s);
                                    }}
                                    style={{ textAlign: 'right' }} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs) var(--spacing-sm)' }}>
                                  <input type="date" className="form-input" value={ps.dueDate} onChange={e => {
                                    const s = [...form.paymentSchedule]; s[i] = { ...s[i], dueDate: e.target.value }; set('paymentSchedule', s);
                                  }} />
                                </td>
                                <td style={{ padding: 'var(--spacing-xs)', textAlign: 'center' }}>
                                  <button onClick={() => {
                                    const s = form.paymentSchedule.filter((_, j) => j !== i);
                                    set('paymentSchedule', s);
                                  }} className="btn btn-secondary btn-sm" style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1 }}>&times;</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                            <button onClick={() => {
                              const newDate = addDays(form.quotationDate, 30 * form.paymentSchedule.length);
                              const s = [...form.paymentSchedule, { percent: '', dueDate: newDate, label: `Installment ${form.paymentSchedule.length + 1}` }];
                              set('paymentSchedule', s);
                            }} className="btn btn-secondary btn-sm">+ Add Installment</button>
                            <button onClick={() => {
                              const s = splitEqually(form.paymentSchedule.length, form.quotationDate);
                              set('paymentSchedule', s);
                            }} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>Re-split Equally</button>
                          </div>
                          {(() => {
                            const totalPct = form.paymentSchedule.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0);
                            return totalPct !== 100 ? (
                              <span style={{ fontSize: 'var(--font-size-xs)', color: '#DC2626', fontWeight: 'var(--font-weight-medium)' }}>
                                Total: {totalPct}% (must be 100%)
                              </span>
                            ) : (
                              <span style={{ fontSize: 'var(--font-size-xs)', color: '#065F46', fontWeight: 'var(--font-weight-medium)' }}>
                                Total: 100%
                              </span>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Account Details */}
              <div className="card mb-md">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="card-header" style={{ borderBottom: editingAccount ? undefined : 'none', marginBottom: editingAccount ? undefined : 0, paddingBottom: editingAccount ? undefined : 0 }}>Account Details</div>
                  <button onClick={() => setEditingAccount(e => !e)} className="btn btn-secondary btn-sm" style={{ padding: '3px 10px', fontSize: '12px' }}>{editingAccount ? 'Done' : 'Edit'}</button>
                </div>
                {editingAccount ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                    <div className="form-group"><label className="form-label">Account Name</label><input className="form-input" value={form.accountName} onChange={e => set('accountName', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Account Number</label><input className="form-input" value={form.accountNumber} onChange={e => set('accountNumber', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Bank</label><input className="form-input" value={form.bankName} onChange={e => set('bankName', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">IFSC</label><input className="form-input" value={form.ifsc} onChange={e => set('ifsc', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Branch</label><input className="form-input" value={form.branch} onChange={e => set('branch', e.target.value)} /></div>
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.8, marginTop: 'var(--spacing-sm)' }}>
                    {form.accountName && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Account Name:</span> {form.accountName}</div>}
                    {form.accountNumber && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Account No:</span> {form.accountNumber}</div>}
                    {form.bankName && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Bank:</span> {form.bankName}</div>}
                    {form.ifsc && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>IFSC:</span> {form.ifsc}</div>}
                    {form.branch && <div><span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Branch:</span> {form.branch}</div>}
                    <div style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>A UPI QR code is also included on the quotation as an alternative.</div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="card mb-md">
                <div className="card-header">Notes</div>
                <textarea className="form-input" style={{ minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional notes or terms..." />
              </div>

              {/* Validation + Actions */}
              {!canDownload && (
                <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  <span style={{ fontSize: '16px' }}>!</span>
                  <span>
                    {!form.customerTo.trim() && <><strong>To</strong> is required. </>}
                    {!form.items.some(it => it.description.trim() && (parseFloat(it.rate) || 0) > 0) && <><strong>At least one item</strong> with description and rate is required.</>}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
                {editingQuotationId ? (
                  <>
                    <button onClick={() => setEditingQuotationId(null)} className="btn btn-secondary">Cancel</button>
                    <button onClick={saveQuotation} disabled={!canDownload || saving} className="btn btn-primary">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={async () => {
                      sessionStorage.removeItem('quotationForm');
                      const f = defaultForm();
                      try {
                        const fy = getFY();
                        const val = await settingsService.get(`quotation_counter_${fy}`);
                        f.quotationNumber = makeQtnNo(fy, (parseInt(val || '0', 10)) + 1);
                      } catch { f.quotationNumber = makeQtnNo(getFY(), 1); }
                      setForm(f);
                    }} className="btn btn-secondary">Clear</button>
                    <button onClick={() => setShowPreview(true)} disabled={!canDownload} className="btn btn-primary">Preview</button>
                  </>
                )}
              </div>
            </>
          )}

          </>}
        </div>
      </div>

      {/* Hidden preview for PDF generation */}
      {!showPreview && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <QuotationPreview
            ref={previewRef}
            form={form}
            subtotal={subtotal}
            discount={discount}
            totalGst={totalGst}
            grandTotal={grandTotal}
            itemSubtotal={itemSubtotal}
            itemGst={itemGst}
            itemTotal={itemTotal}
          />
        </div>
      )}

      {dlQuotation && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <QuotationPreview
            ref={dlPreviewRef}
            form={dlQuotation.formData}
            subtotal={parseFloat(dlQuotation.subtotal)}
            discount={parseFloat(dlQuotation.formData?.discount) || 0}
            totalGst={parseFloat(dlQuotation.totalGst)}
            grandTotal={parseFloat(dlQuotation.grandTotal)}
            itemSubtotal={itemSubtotal}
            itemGst={itemGst}
            itemTotal={itemTotal}
          />
        </div>
      )}

      {!embedded && <Footer />}
    </>
  );
};

export default Quotations;
